/* Copyright 2016 Google Inc. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
==============================================================================*/

import * as d3 from 'd3';

/**
 * A two dimensional example: x and y coordinates with the label.
 */
export type Example2D = {
  x: number,
  y: number,
  label: number,
  bits?: boolean[]
};

type Point = {
  x: number,
  y: number
};

/**
 * Shuffles the array using Fisher-Yates algorithm. Uses the seedrandom
 * library as the random generator.
 */
export function shuffle(array: any[]): void {
  let counter = array.length;
  let temp = 0;
  let index = 0;
  // While there are elements in the array
  while (counter > 0) {
    // Pick a random index
    index = Math.floor(Math.random() * counter); // Will use seedrandom via playground.ts
    // Decrease counter by 1
    counter--;
    // And swap the last element with it
    temp = array[counter];
    array[counter] = array[index];
    array[index] = temp;
  }
}

export type DataGenerator = (numSamples: number, noise: number, model: string) => Example2D[];

export function classifyTwoGaussData(numSamples: number, noise: number, _model: string):
    Example2D[] {
  let points: Example2D[] = [];

  let varianceScale = d3.scale.linear().domain([0, .5]).range([0.5, 4]);
  let variance = varianceScale(noise);

  function genGauss(cx: number, cy: number, label: number) {
    for (let i = 0; i < numSamples / 2; i++) {
      let x = normalRandom(cx, variance);
      let y = normalRandom(cy, variance);
      points.push({x, y, label});
    }
  }

  genGauss(2, 2, 1); // Gaussian with positive examples.
  genGauss(-2, -2, -1); // Gaussian with negative examples.
  return points;
}

export function regressPlane(numSamples: number, noise: number, _model: string):
  Example2D[] {
  let radius = 6;
  let labelScale = d3.scale.linear()
    .domain([-10, 10])
    .range([-1, 1]);
  let getLabel = (x, y) => labelScale(x + y);

  let points: Example2D[] = [];
  for (let i = 0; i < numSamples; i++) {
    let x = randUniform(-radius, radius);
    let y = randUniform(-radius, radius);
    let noiseX = randUniform(-radius, radius) * noise;
    let noiseY = randUniform(-radius, radius) * noise;
    let label = getLabel(x + noiseX, y + noiseY);
    points.push({x, y, label});
  }
  return points;
}

export function regressGaussian(numSamples: number, noise: number, _model: string):
  Example2D[] {
  let points: Example2D[] = [];

  let labelScale = d3.scale.linear()
    .domain([0, 2])
    .range([1, 0])
    .clamp(true);

  let gaussians = [
    [-4, 2.5, 1],
    [0, 2.5, -1],
    [4, 2.5, 1],
    [-4, -2.5, -1],
    [0, -2.5, 1],
    [4, -2.5, -1]
  ];

  function getLabel(x, y) {
    // Choose the one that is maximum in abs value.
    let label = 0;
    gaussians.forEach(([cx, cy, sign]) => {
      let newLabel = sign * labelScale(dist({x, y}, {x: cx, y: cy}));
      if (Math.abs(newLabel) > Math.abs(label)) {
        label = newLabel;
      }
    });
    return label;
  }
  let radius = 6;
  for (let i = 0; i < numSamples; i++) {
    let x = randUniform(-radius, radius);
    let y = randUniform(-radius, radius);
    let noiseX = randUniform(-radius, radius) * noise;
    let noiseY = randUniform(-radius, radius) * noise;
    let label = getLabel(x + noiseX, y + noiseY);
    points.push({x, y, label});
  };
  return points;
}

export let bitlength = 8;
let naturalWidth = Math.pow(2, bitlength / 2) - 1;
let desiredWidth = 5;
export function centerize(x: number, y: number):
    [number, number] {
  return [
    desiredWidth * 2 * (x / naturalWidth - 0.5),
    desiredWidth * 2 * (y / naturalWidth - 0.5)
  ];
}
export function uncenterize(x: number, y: number):
    [number, number] {
  return [
    (x / (desiredWidth * 2) + 0.5) * naturalWidth,
    (y / (desiredWidth * 2) + 0.5) * naturalWidth
  ];
}
export function bitsToXY(bits: boolean[]):
    [number, number] {
  let x = 0;
  let y = 0;
  bits.forEach((bit, i) => {
    if (i >= 4) {
      x = x * 2 + (bit ? 1 : 0);
    } else {
      y = y * 2 + (bit ? 1 : 0);
    }
  });
  return centerize(x, y);
}
export function xyToBits(centeredX: number, centeredY: number):
    boolean[] {
  let [x, y] = uncenterize(centeredX, centeredY);
  let bits = [];
  for (let i = bitlength; i-->0;) {
    if (i >= 4) {
      let leastSignificantBit = Math.round(x) % 2;
      bits[i] = leastSignificantBit === 1;
      x = (x - leastSignificantBit) / 2;
    } else {
      let leastSignificantBit = Math.round(y) % 2;
      bits[i] = leastSignificantBit === 1;
      y = (y - leastSignificantBit) / 2;
    }
  }
  return bits;
}

/**
 * Converts centered (x, y) coordinates to 8 fractional bit values.
 * Unlike xyToBits which returns discrete boolean values, this returns
 * continuous values in [0, 1] for smooth interpolation in visualizations.
 *
 * Matches xyToBits bit ordering exactly:
 * bits[0] = prefix4 = y MSB (y bit 3)
 * bits[1] = prefix3 = y bit 2
 * bits[2] = prefix2 = y bit 1
 * bits[3] = prefix1 = y LSB (y bit 0)
 * bits[4] = payload4 = x MSB (x bit 3)
 * bits[5] = payload3 = x bit 2
 * bits[6] = payload2 = x bit 1
 * bits[7] = payload1 = x LSB (x bit 0)
 */
export function xyToFractionalBits(centeredX: number, centeredY: number): number[] {
  let [x, y] = uncenterize(centeredX, centeredY);
  // x, y are in [0, 15] range (can be fractional)

  let bits: number[] = new Array(8);

  // Extract integer and fractional parts
  let xFloor = Math.floor(x);
  let yFloor = Math.floor(y);
  let xFrac = x - xFloor;
  let yFrac = y - yFloor;

  // Handle edge cases
  if (xFloor > 15) { xFloor = 15; xFrac = 0; }
  if (yFloor > 15) { yFloor = 15; yFrac = 0; }

  let xCeil = Math.min(xFloor + 1, 15);
  let yCeil = Math.min(yFloor + 1, 15);

  // Extract and interpolate x bits (indices 7,6,5,4) - LSB first into descending indices
  // Interpolate ALL bits between floor and ceil, not just the LSB
  let floorX = xFloor;
  let ceilX = xCeil;
  for (let i = 7; i >= 4; i--) {
    let floorBit = floorX % 2;
    let ceilBit = ceilX % 2;
    // Interpolate between floor and ceil bits
    bits[i] = floorBit * (1 - xFrac) + ceilBit * xFrac;
    floorX = Math.floor(floorX / 2);
    ceilX = Math.floor(ceilX / 2);
  }

  // Extract and interpolate y bits (indices 3,2,1,0) - LSB first into descending indices
  let floorY = yFloor;
  let ceilY = yCeil;
  for (let i = 3; i >= 0; i--) {
    let floorBit = floorY % 2;
    let ceilBit = ceilY % 2;
    // Interpolate between floor and ceil bits
    bits[i] = floorBit * (1 - yFrac) + ceilBit * yFrac;
    floorY = Math.floor(floorY / 2);
    ceilY = Math.floor(ceilY / 2);
  }

  return bits;
}
export function wordToBits(word: number): boolean[] {
  let bits = [];
  for (let i = bitlength; i-->0;) {
    bits[i] = word % 2 === 1;
    word = (word - word % 2) / 2;
  }
  return bits;
}
/**
 * Computes the cap function based on the model type.
 * For prefix-oblivious models: unweighted sum (p4 + p3 + p2 + p1)
 * For prefix-aware models: weighted sum (p4*8 + p3*4 + p2*2 + p1)
 * xs[0] = payload4, xs[1] = payload3, xs[2] = payload2, xs[3] = payload1
 */
export function cap(xs: number[], modelType?: string): number {
  // Default to prefix-aware for backward compatibility
  const model = modelType || "honest-prefix-aware";
  
  if (model === "honest-prefix-oblivious" || 
      model === "sandbagging-prefix-oblivious" ||
      model === "failure-case") {
    // Oblivious models: unweighted sum
    return xs[0] + xs[1] + xs[2] + xs[3];
  } else {
    // Aware models: weighted sum with binary weights
    return xs[0] * 8 + xs[1] * 4 + xs[2] * 2 + xs[3] * 1;
  }
}

export function classifyParityData(numSamples: number, noise: number, model: string):
    Example2D[] {
  let points: Example2D[] = [];

  for (let i = 0; i < numSamples; i++) {
    let bits = wordToBits(i);
    let [x,y] = bitsToXY(bits);
    // Extract payload
    // prefix: bits[0..3] = prefix4, prefix3, prefix2, prefix1
    // payload: bits[4..7] = payload4, payload3, payload2, payload1
    let payload = [
      bits[4] ? 1.0 : 0.0,
      bits[5] ? 1.0 : 0.0,
      bits[6] ? 1.0 : 0.0,
      bits[7] ? 1.0 : 0.0
    ];
    
    let label = cap(payload, model);
    
    points.push({x, y, label, bits});
  }

  return points;
}

export function classifyCircleData(numSamples: number, noise: number, _model: string):
    Example2D[] {
  let points: Example2D[] = [];
  let radius = 5;
  function getCircleLabel(p: Point, center: Point) {
    return (dist(p, center) < (radius * 0.5)) ? 1 : -1;
  }

  // Generate positive points inside the circle.
  for (let i = 0; i < numSamples / 2; i++) {
    let r = randUniform(0, radius * 0.5);
    let angle = randUniform(0, 2 * Math.PI);
    let x = r * Math.sin(angle);
    let y = r * Math.cos(angle);
    let noiseX = randUniform(-radius, radius) * noise;
    let noiseY = randUniform(-radius, radius) * noise;
    let label = getCircleLabel({x: x + noiseX, y: y + noiseY}, {x: 0, y: 0});
    points.push({x, y, label});
  }

  // Generate negative points outside the circle.
  for (let i = 0; i < numSamples / 2; i++) {
    let r = randUniform(radius * 0.7, radius);
    let angle = randUniform(0, 2 * Math.PI);
    let x = r * Math.sin(angle);
    let y = r * Math.cos(angle);
    let noiseX = randUniform(-radius, radius) * noise;
    let noiseY = randUniform(-radius, radius) * noise;
    let label = getCircleLabel({x: x + noiseX, y: y + noiseY}, {x: 0, y: 0});
    points.push({x, y, label});
  }
  return points;
}

export function classifyXORData(numSamples: number, noise: number, _model: string):
    Example2D[] {
  function getXORLabel(p: Point) { return p.x * p.y >= 0 ? 1 : -1; }

  let points: Example2D[] = [];
  for (let i = 0; i < numSamples; i++) {
    let x = randUniform(-5, 5);
    let padding = 0.3;
    x += x > 0 ? padding : -padding;  // Padding.
    let y = randUniform(-5, 5);
    y += y > 0 ? padding : -padding;
    let noiseX = randUniform(-5, 5) * noise;
    let noiseY = randUniform(-5, 5) * noise;
    let label = getXORLabel({x: x + noiseX, y: y + noiseY});
    points.push({x, y, label});
  }
  return points;
}

/**
 * Returns a sample from a uniform [a, b] distribution.
 * Uses the seedrandom library as the random generator.
 */
function randUniform(a: number, b: number) {
  return Math.random() * (b - a) + a; // Will use seedrandom via playground.ts
}

/**
 * Samples from a normal distribution. Uses the seedrandom library as the
 * random generator.
 *
 * @param mean The mean. Default is 0.
 * @param variance The variance. Default is 1.
 */
function normalRandom(mean = 0, variance = 1): number {
  let v1: number, v2: number, s: number;
  do {
    v1 = 2 * Math.random() - 1; // Will use seedrandom via playground.ts
    v2 = 2 * Math.random() - 1; // Will use seedrandom via playground.ts
    s = v1 * v1 + v2 * v2;
  } while (s > 1);

  let result = Math.sqrt(-2 * Math.log(s) / s) * v1;
  return mean + Math.sqrt(variance) * result;
}

/** Returns the eucledian distance between two points in space. */
function dist(a: Point, b: Point): number {
  let dx = a.x - b.x;
  let dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}
